// THE EVENT NAMES — the discriminator every hive payload carries and every read seam
// narrows on. One name per shape (rule 1 in the barrel's header).
// Split out of `../hive-event.ts`, which is now the barrel over this directory.

/**
 * Every hive event name. ONE name per shape (rule 1 in `../hive-event.ts`'s header) — a name is
 * the discriminator a read seam narrows on, so two shapes under one name is a read seam
 * that cannot narrow at all.
 *
 * `ScoutTreeGap` and `ScoutSurveyFailed` are the two that used to be spelled
 * `scout_survey`. 69 of 685 prod rows are the tree-contract refusal
 * (`{event: 'scout_survey', lane, ok: false, requires, tree_gap, …}`,
 * `apps/runner/src/tree-contract.ts` + `modes/scout/executor.ts`) and a further leg is the
 * thrown-survey catch (`{event: 'scout_survey', ok: false, error}`). Neither has a single
 * field in common with a survey round beyond `event` and `ok`. Renaming them is what makes
 * the read seam able to say "this is a tree-gap refusal" instead of "unparseable"; the
 * historical rows keep the old name and are history.
 */
export const HiveEvent = {
  /** The coding lane's surveyed manifest, emitted BEFORE dispatch. */
  TeamManifest: 'team_manifest',
  /** The coding lane's editor fan-out, emitted once the round settles. */
  TeamFanOut: 'team_fanout',
  /** A bounded second editor wave after the gate went red. Producer: `team/fix-wave.ts`. */
  TeamFixWave: 'team_fix_wave',
  /** The scout lane's survey, summed over its rounds. */
  ScoutSurvey: 'scout_survey',
  /** The scout lane refused before dispatching: the checkout is not the tree it requires. */
  ScoutTreeGap: 'scout_tree_gap',
  /** The scout survey threw. The run continues ungrounded; the reason is the record. */
  ScoutSurveyFailed: 'scout_survey_failed',
  /** The review lane's group fan-out decision. */
  ReviewFanOut: 'review_fanout',
  /** The debugger lane's probe fan-out. Producer: plan 102 slice 05. */
  DevopsFanOut: 'devops_fanout',
} as const;
export type HiveEvent = (typeof HiveEvent)[keyof typeof HiveEvent];
